import React, { ReactNode } from 'react';
import { useUserRolesContext } from '../../context/userRolesContext';
import Loader from '../ui/loader';

// Tipos para el componente
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requireAllRoles?: boolean;
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Componente para proteger rutas basado en roles de usuario
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAllRoles = false,
  fallback = null,
  redirectTo
}) => {
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles
  } = useUserRolesContext();

  // Mostrar loader mientras se cargan los datos
  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  // Si no está autenticado, mostrar fallback
  if (!isAuthenticated) {
    if (redirectTo) {
      window.location.href = redirectTo;
      return null;
    }
    return <>{fallback}</>;
  }

  // Si no hay roles requeridos, permitir acceso
  if (requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Verificar permisos según la configuración
  let hasPermission = false;

  if (requireAllRoles) {
    // Requiere todos los roles especificados
    hasPermission = hasAllRoles(requiredRoles);
  } else {
    // Requiere al menos uno de los roles especificados
    hasPermission = hasAnyRole(requiredRoles);
  }

  // Si tiene permisos, mostrar contenido
  if (hasPermission) {
    return <>{children}</>;
  }

  // Si no tiene permisos, mostrar fallback o redirigir
  if (redirectTo) {
    window.location.href = redirectTo;
    return null;
  }

  return <>{fallback}</>;
};

/**
 * Componente para mostrar contenido condicionalmente basado en roles
 */
interface ConditionalRenderProps {
  children: ReactNode;
  requiredRoles?: string[];
  requireAllRoles?: boolean;
  fallback?: ReactNode;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  requiredRoles = [],
  requireAllRoles = false,
  fallback = null
}) => {
  const {
    isAuthenticated,
    hasAnyRole,
    hasAllRoles
  } = useUserRolesContext();

  // Si no está autenticado, no mostrar nada
  if (!isAuthenticated) {
    return <>{fallback}</>;
  }

  // Si no hay roles requeridos, mostrar contenido
  if (requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Verificar permisos
  let hasPermission = false;

  if (requireAllRoles) {
    hasPermission = hasAllRoles(requiredRoles);
  } else {
    hasPermission = hasAnyRole(requiredRoles);
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

/**
 * Hook para verificar permisos en componentes
 */
export const usePermissions = () => {
  const {
    isAuthenticated,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    userRoles
  } = useUserRolesContext();

  return {
    isAuthenticated,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    userRoles,

    // Funciones de conveniencia
    isAdmin: () => hasRole('admin') || hasRole('administrator'),
    isUser: () => hasRole('user'),
    canEdit: () => hasAnyRole(['admin', 'editor', 'moderator']),
    canDelete: () => hasAnyRole(['admin', 'superadmin']),
    canView: () => hasAnyRole(['admin', 'user', 'viewer'])
  };
};

/**
 * Componente para guardar UI basado en permisos (no roles)
 */
interface PermissionGuardProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  requireAllPermissions?: boolean;
  fallback?: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions,
  requireAllPermissions = false,
  fallback = null
}) => {
  const {
    isAuthenticated,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  } = useUserRolesContext();

  // Mostrar nada mientras carga
  if (isLoading) {
    return null;
  }

  // Si no está autenticado, no mostrar nada
  if (!isAuthenticated) {
    return <>{fallback}</>;
  }

  // Si no hay permisos especificados, mostrar contenido
  if (!permission && (!permissions || permissions.length === 0)) {
    return <>{children}</>;
  }

  // Verificar permisos
  let hasAccess = false;

  if (permission) {
    // Verificar un solo permiso
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Verificar múltiples permisos
    if (requireAllPermissions) {
      hasAccess = hasAllPermissions(permissions);
    } else {
      hasAccess = hasAnyPermission(permissions);
    }
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};